import type { ObjectId } from "@novorender/api";
import type { ReadonlyVec2, ReadonlyVec3 } from "gl-matrix";
import { useCallback } from "react";

import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { lastPickSampleActions, useDispatchLastPickSample } from "contexts/lastPickSample";
import { measureActions, selectMeasure, selectMeasurePickSettings } from "features/measure";
import { selectCurrentPickerGeneratedParametricData } from "features/render";
import type { PointSnapInfo } from "features/render/measureSnap";
import { applyCameraDistanceToMeasureTolerance } from "features/render/utils";
import type { ExtendedMeasureEntity } from "types/misc";

import type { PointLinePoint } from "../pointLineSlice";
import {
    pointLineActions,
    selectDraggingPoint,
    selectLockPointLineElevation,
    selectLockPointLineVertical,
    selectPointLineCurrentIdx,
    selectPointLines,
    selectPointLineSnapToPoint,
} from "../pointLineSlice";
import { pushCreateLineUndoRedo, pushExtendLineUndoRedo } from "../pointLineUndoRedo";
import { getPointLineSnapInfo } from "../utils";

export function useAddPointToPointLine() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const measure = useAppSelector(selectMeasure);
    const lockPointLineElevation = useAppSelector(selectLockPointLineElevation);
    const lockPointLineVertical = useAppSelector(selectLockPointLineVertical);
    const measurePickSettings = useAppSelector(selectMeasurePickSettings);
    const allowGeneratedParametric = useAppSelector(selectCurrentPickerGeneratedParametricData);
    const snapTo = useAppSelector(selectPointLineSnapToPoint);
    const pointLines = useAppSelector(selectPointLines);
    const currentIndex = useAppSelector(selectPointLineCurrentIdx);
    const draggingPoint = useAppSelector(selectDraggingPoint);

    const dispatch = useAppDispatch();
    const dispatchLastPickSample = useDispatchLastPickSample();
    const appStore = useAppStore();

    const addPointToPointLine = useCallback(
        async function addPointToPointLineImpl({
            position,
            position2d,
            objectId,
            move,
            pointLineId,
            pointId,
            // On desktop it comes from hover, but on mobile there's no hover,
            // so pass it explicitly
            measureEntity = measure.hover,
        }: {
            position: ReadonlyVec3;
            position2d: ReadonlyVec2;
            objectId: ObjectId;
            move?: { lineIndex: number; pointIndex: number };
            pointLineId?: string;
            pointId?: string;
            measureEntity?: ExtendedMeasureEntity;
        }) {
            if (!view) {
                return;
            }

            const handleSnapTo = (snapTo: number) => {
                const pointLine = pointLines[move?.lineIndex ?? currentIndex];
                point = pointLine.points[snapTo];
                closeLine = true;
            };

            let closeLine = false;
            let point: Omit<PointLinePoint, "id"> = {
                position,
                snap: { type: "vertex", connection: position },
                objectId,
            };
            if (snapTo !== null) {
                handleSnapTo(snapTo);
            } else if (measureEntity?.connectionPoint) {
                let snap: PointSnapInfo | undefined;
                if ((!lockPointLineElevation && !lockPointLineVertical) || measureEntity.drawKind !== "vertex") {
                    const tolerance = applyCameraDistanceToMeasureTolerance(
                        position,
                        view.renderState.camera,
                        measurePickSettings,
                    );
                    const entity = await view.measure?.core.pickMeasureEntity(
                        objectId,
                        measureEntity.connectionPoint,
                        tolerance,
                        allowGeneratedParametric.enabled,
                    );
                    const snapToPoint = await getPointLineSnapInfo({
                        view,
                        position2d,
                        hoverEnt: entity,
                        draggingPoint,
                        currentPointLineIdx: currentIndex,
                        pointLines,
                    });
                    if (snapToPoint.snapInfo) {
                        snap = snapToPoint.snapInfo;
                    }
                    if (snapToPoint.snapTo !== null) {
                        handleSnapTo(snapToPoint.snapTo);
                    }
                }

                point = {
                    position: measureEntity.connectionPoint,
                    snap: snap ?? { type: "vertex", connection: measureEntity.connectionPoint },
                    objectId,
                };
            } else {
                const snapToPoint = await getPointLineSnapInfo({
                    view,
                    position2d,
                    hoverEnt: undefined,
                    draggingPoint,
                    currentPointLineIdx: currentIndex,
                    pointLines,
                });
                if (snapToPoint.snapInfo) {
                    point = {
                        position: snapToPoint.snapInfo.connection,
                        snap: snapToPoint.snapInfo,
                        objectId,
                    };
                }
                if (snapToPoint.snapTo !== null) {
                    handleSnapTo(snapToPoint.snapTo);
                }
            }

            if (move) {
                const pointLine = pointLines[move.lineIndex];
                const pointWithId: PointLinePoint = {
                    ...point,
                    id: pointLine.points[move.pointIndex].id,
                };
                dispatch(
                    pointLineActions.movePoint(
                        { point: pointWithId, lineIdx: move.lineIndex, pointIdx: move.pointIndex },
                        view,
                    ),
                );
            } else {
                const currentLine = pointLines[currentIndex];
                const numPointsBeforeUpdate = currentLine?.points.length ?? 0;
                const pointIdToUse = pointId ?? crypto.randomUUID();
                const lineIdToUse = pointLineId ?? (numPointsBeforeUpdate === 0 ? crypto.randomUUID() : undefined);
                dispatch(pointLineActions.addPoint(point, view, pointIdToUse, lineIdToUse, currentLine.reversed));
                // For new point lines
                if (numPointsBeforeUpdate >= 1) {
                    const state = appStore.getState();
                    const updatedPointLines = selectPointLines(state);
                    const updatedCurrentPointLine = updatedPointLines[currentIndex];
                    const isLineReversed = updatedCurrentPointLine.reversed;
                    if (updatedCurrentPointLine) {
                        const addedPoint = updatedCurrentPointLine.points[updatedCurrentPointLine.points.length - 1];
                        if (numPointsBeforeUpdate === 1) {
                            pushCreateLineUndoRedo(dispatch, view, updatedCurrentPointLine);
                        } else {
                            pushExtendLineUndoRedo(
                                dispatch,
                                view,
                                updatedCurrentPointLine.id,
                                addedPoint,
                                closeLine,
                                isLineReversed,
                            );
                        }
                    }
                }
            }
            dispatch(pointLineActions.setPointLineSnapToPoint(null));
            dispatch(measureActions.selectHoverObj(undefined));
            if (closeLine) {
                dispatch(pointLineActions.newPointLine());
            }
            dispatchLastPickSample(lastPickSampleActions.set(null));
        },
        [
            dispatch,
            dispatchLastPickSample,
            lockPointLineElevation,
            lockPointLineVertical,
            measure.hover,
            measurePickSettings,
            view,
            allowGeneratedParametric,
            snapTo,
            pointLines,
            currentIndex,
            draggingPoint,
            appStore,
        ],
    );
    return addPointToPointLine;
}
